/* eslint-disable react-hooks/exhaustive-deps */
import { has } from '@/commonFunctions/utils'
import { Button } from '@/components/button'
import { Tabs } from '@/components/common/Tab'
import { MainContent } from '@/components/content'
import { ArrowBack, ChevronLeft, People } from '@/components/icons'
import { MainLayout } from '@/components/layouts'
import { MainSidebar, MobileSidebar } from '@/components/sidebar'
import { EventDetailHeaderLoading } from '@/components/skeleton'
import { EVENT_MODIFY, LIMIT, OFFSET } from '@/constants/Common'
import { ROUTE_EVENTS } from '@/constants/Routes'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { getClubRoles } from '@/pages/club/clubSlice'
import { clubSelector } from '@/pages/club/selectors'
import { appSelector } from '@/pages/home/selectors'
import { userSelector } from '@/pages/user/selectors'
import { setVerifyEmailModalState } from '@/pages/user/userSlice'
import { useGetBracketMatches, useGetBracketPlayers, useGetBracketTeams } from '@/services'
import { Tab } from '@/types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMediaQuery } from 'usehooks-ts'
import { BracketHeader } from '../components/BracketHeader'
import { BracketInfo } from '../components/BracketInfo'
import { BracketMatches } from '../components/BracketMatches'
import { BracketPayments } from '../components/BracketPayments'
import { BracketPlayers } from '../components/BracketPlayers'
import { BracketQueues } from '../components/BracketQueues'
import { BracketStandings } from '../components/BracketStandings'
import { BracketTeams } from '../components/BracketTeams'
import { BracketUnmatchedPlayers } from '../components/BracketUnmatchedPlayers'
import { getBracketDetail, getEventDetail } from '../eventSlice'
import { eventSelector } from '../selectors'

export const BracketDetail = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const params = useParams()
  const dispatch = useAppDispatch()
  const { isShowDropdown } = useAppSelector(appSelector)
  const { permissions } = useAppSelector(clubSelector)
  const { token } = useAppSelector(authSelector)
  const { event, isLoading, bracket } = useAppSelector(eventSelector)
  const { user } = useAppSelector(userSelector)
  const isMobile = useMediaQuery('(max-width: 767px)')

  const { eventId, bracketId } = params

  const currencySymbol = event?.currencyDetails?.currencySymbol

  const { data: resPlayers } = useGetBracketPlayers(
    {
      limit: LIMIT,
      offset: OFFSET,
      bracketId: Number(bracketId),
      query: '*',
      filters: {
        isRegistered: false,
        isWaitListed: false,
      },
      sort: null,
    },
    user?.id
  )

  const { mutate: getTeams, data: resTeams } = useGetBracketTeams()
  const { mutate: getMatches, data: resMatches } = useGetBracketMatches()

  const [tabs, setTabs] = useState<Tab | null>(null)
  const [playersLength, setPlayersLength] = useState(0)

  const handleBackToEvent = () => {
    navigate(`${ROUTE_EVENTS}/${event?.leagueId}`)
  }

  const onGetPlayersLength = (length: number) => {
    setPlayersLength(length)
  }

  const onCallBack = () => {
    if (bracketId) {
      dispatch(getBracketDetail(bracketId))
    }
  }

  useEffect(() => {
    if (user && !user?.isValidEmail) {
      dispatch(setVerifyEmailModalState(true))
    }
  }, [user])

  useEffect(() => {
    if (eventId) {
      dispatch(getEventDetail(eventId))
    }

    if (bracketId) {
      dispatch(getBracketDetail(bracketId))
    }
  }, [eventId, bracketId, token])

  useEffect(() => {
    if (!permissions && user?.id && event) {
      dispatch(getClubRoles({ clubId: event?.clubId, userId: user?.id }))
    }
  }, [user, event])

  useEffect(() => {
    if (isMobile && bracket && token) {
      getTeams({
        format: bracket.format,
        bracketId: Number(bracketId),
        limit: LIMIT,
        offset: OFFSET,
      })
      getMatches({
        round: 0,
        bracketId: Number(bracketId),
        limit: LIMIT,
        offset: OFFSET,
        tags: [],
      })
    }
  }, [isMobile, bracket])

  useEffect(() => {
    if (bracket && permissions) {
      const isShowQueue = bracket?.elimination === 'COMPASS' && bracket?.isMatchSeeded
      const isShowStanding =
        bracket?.durationStatus !== 'UPCOMING' && bracket?.elimination === 'ROUND_ROBIN'
          ? bracket?.hasConfirmMatch
          : bracket?.canShowStandings

      setTabs({
        Queue: {
          tabIsUse: !!isShowQueue,
          isShow: isShowQueue,
        },
        Standings: {
          tabIsUse: !isShowQueue && !!isShowStanding,
          isShow: isShowStanding,
        },
        Players: { tabIsUse: !isShowQueue && !isShowStanding, isShow: true },
        Teams: { tabIsUse: false, isShow: bracket?.format === 'DOUBLES' },
        UnmatchedPlayer: { tabIsUse: false, isShow: bracket?.format === 'DOUBLES' && has(permissions, EVENT_MODIFY) },
        Matches: { tabIsUse: false, isShow: true },
        Payments: { tabIsUse: false, isShow: has(permissions, EVENT_MODIFY) },
      })
    }
  }, [bracket, permissions])

  return (
    <>
      {!isShowDropdown && <MobileSidebar />}
      <MainLayout>
        <MainSidebar />
        <MainContent>
          <div className="flex flex-col gap-y-6 md:gap-y-8 mb-20 lg:mb-6">
            <div className="hidden md:flex items-center gap-x-1">
              <Link to={`${ROUTE_EVENTS}/${event?.leagueId}`}>
                <h3 className="text-xl text-white font-semibold text-opacity-50 max-w-xs text-ellipsis whitespace-nowrap overflow-x-hidden">
                  {event?.leagueName}
                </h3>
              </Link>
              <ChevronLeft className="text-white" />
              <h1 className="text-xl text-white font-semibold">{t('event.bracketDetails')}</h1>
            </div>

            <div className="flex md:hidden items-center gap-x-3">
              <Button onClick={handleBackToEvent} className="hover:opacity-50">
                <ArrowBack className="text-white" />
              </Button>
              <div className="flex flex-col">
                <span className="text-sm text-white font-semibold max-w-[calc(100%-200px)] text-ellipsis whitespace-nowrap overflow-x-hidden">
                  {event?.leagueName}
                </span>
                <span className="text-sm text-white font-semibold">{t('event.bracketDetails')}</span>
              </div>
            </div>

            {isLoading ? (
              <EventDetailHeaderLoading />
            ) : (
              <BracketHeader playersLength={playersLength} onCallBack={onCallBack} />
            )}

            {/* render count for mobile */}
            {token && (
              <div className="flex md:hidden gap-x-3">
                <div className="flex-1 p-2 rounded-lg bg-white bg-opacity-20 flex flex-col items-center justify-center">
                  <div className="flex items-center gap-x-1">
                    <People className="text-white" />
                    <span className="text-white text-xs font-medium">{t('event.players')}</span>
                  </div>
                  <span className="text-white font-bold text-2xl">{resPlayers?.result?.total}</span>
                </div>

                <div className="flex-1 p-2 rounded-lg bg-white bg-opacity-20 flex flex-col items-center justify-center">
                  <div className="flex items-center gap-x-1">
                    <People className="text-white" />
                    <span className="text-white text-xs font-medium">{t('event.teams')}</span>
                  </div>
                  <span className="text-white font-bold text-2xl">{resTeams?.result?.total}</span>
                </div>

                <div className="flex-1 p-2 rounded-lg bg-white bg-opacity-20 flex flex-col items-center justify-center">
                  <div className="flex items-center gap-x-1">
                    <People className="text-white" />
                    <span className="text-white text-xs font-medium">{t('event.matches')}</span>
                  </div>
                  <span className="text-white font-bold text-2xl">{resMatches?.result?.total}</span>
                </div>
              </div>
            )}

            {/* render detail for desktop */}
            {token && user?.isValidEmail && (
              <div className="flex flex-col gap-y-4">
                <h2 className="text-xl text-white font-semibold">{t('common.details')}</h2>
                <div className="flex gap-x-6">
                  {tabs && (
                    <div className="hidden md:flex md:flex-col md:w-2/3 gap-y-4">
                      <Tabs tabs={tabs} setTabs={setTabs} />

                      {tabs.Queue.tabIsUse && <BracketQueues />}
                      {tabs.Standings.tabIsUse && bracket && <BracketStandings />}
                      {tabs.Players.tabIsUse && (
                        <BracketPlayers onReturnPlayersLength={(length) => onGetPlayersLength(length)} />
                      )}
                      {tabs.Teams.tabIsUse && permissions && <BracketTeams permissions={permissions} />}
                      {tabs.UnmatchedPlayer.tabIsUse && <BracketUnmatchedPlayers />}
                      {tabs.Matches.tabIsUse && <BracketMatches />}
                      {tabs.Payments.tabIsUse && bracket && (
                        <BracketPayments leagueId={bracket?.leagueId} currency={currencySymbol} />
                      )}
                    </div>
                  )}
                  {event && bracket && <BracketInfo event={event} bracket={bracket} />}
                </div>
              </div>
            )}

            {!token && (
              <div>
                <p className="text-white text-opacity-70 text-sm font-semibold">{t('common.signUpOrLogin')}</p>
              </div>
            )}
          </div>
        </MainContent>
      </MainLayout>
    </>
  )
}
